import { useState, useEffect } from 'react';
import { isGCSUrl, isLocalAssetUrl } from '../lib/imageProxyUtils';

/**
 * Hook to get image URL for display. GCS and local URLs are used as-is.
 * Non-GCS URLs (e.g. Instagram CDN) are not proxied; show default placeholder instead.
 * @param url - The original image URL
 * @param postId - The post ID
 * @param mediaType - Type of media: 'thumbnail' or 'profile_image'
 * @param platform - Post platform ('INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT').
 * @returns The URL to display, or null to show default placeholder (when not GCS/local)
 */
export const useImageProxy = (
  url: string | null | undefined,
  postId: number | null | undefined,
  mediaType: 'thumbnail' | 'profile_picture',
  platform?: string | null
): string | null => {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);

  // Non-GCS Instagram: show placeholder (no proxy API call)
  const usePlaceholder =
    !!url &&
    !!postId &&
    platform === 'INSTAGRAM' &&
    !isGCSUrl(url) &&
    !isLocalAssetUrl(url);

  useEffect(() => {
    setDisplayUrl(null);

    if (!url || !postId) {
      return;
    }

    if (isGCSUrl(url) || isLocalAssetUrl(url)) {
      setDisplayUrl(url);
      return;
    }

    if (platform != null && platform !== 'INSTAGRAM') {
      setDisplayUrl(url);
      return;
    }

    setDisplayUrl(null);
  }, [url, postId, mediaType, platform]);

  if (usePlaceholder) return null;
  return displayUrl ?? url ?? null;
};

